/**
 * ListsStatsTab utilities - tag resolution for list rows.
 * For Media Devoted | Created By Ciro Autiero
 */

type LeadsListRef = { id: number; name?: string; voluumId?: string | null };

/** Case-insensitive lookup in a record keyed by list id (wiserUid/voluumId). */
function lookupTagsByKey<T>(
  record: Record<string, T>,
  key?: string | null,
): T | undefined {
  const k = key?.trim();
  if (!k) return undefined;
  if (record[k]) return record[k];
  return Object.entries(record).find(
    ([rk]) => rk.toLowerCase() === k.toLowerCase(),
  )?.[1];
}

/** Finds the leads list whose display name matches the leaf's label (or raw id). */
function findLeadsListForLeaf(
  leaf: { leafId?: string; label?: string },
  leadsLists: LeadsListRef[],
): LeadsListRef | undefined {
  const names = [leaf.label, leaf.leafId]
    .map((s) => s?.trim().toLowerCase())
    .filter((s): s is string => !!s);
  if (names.length === 0) return undefined;
  return leadsLists.find((l) => {
    const name = (l.name ?? '').trim().toLowerCase();
    return name !== '' && names.includes(name);
  });
}

/**
 * Resolves tags for a Source ▸ Leads list tree leaf. leafId is the raw CV4 /
 * token_4 value (= wiser_uid) so tagsByListId usually hits directly; the list
 * name fallback covers leaves where only the resolved label is available.
 */
export function resolveTagsForListLeaf(
  leaf: { leafId?: string; label?: string },
  tagsByListId: Record<string, string[]>,
  tagsByWiserId: Record<number, string[]>,
  leadsLists: LeadsListRef[],
): string[] {
  const direct = resolveTagsForListRow(
    { listId: leaf.leafId, listName: leaf.label },
    tagsByListId,
    tagsByWiserId,
    leadsLists,
  );
  if (direct.length > 0) return direct;
  const lead = findLeadsListForLeaf(leaf, leadsLists);
  if (!lead) return [];
  const byWiserId = tagsByWiserId[lead.id];
  if (byWiserId?.length) return byWiserId;
  return lookupTagsByKey(tagsByListId, lead.voluumId) ?? [];
}

/** Best-effort per-rule date-range details for a tree leaf (same matching as tags). */
export function resolveTagDetailsForListLeaf(
  leaf: { leafId?: string; label?: string },
  tagDetailsByListId: Record<string, Array<{ tag: string; dateRangePreset: string }>>,
  leadsLists: LeadsListRef[],
): Array<{ tag: string; dateRangePreset: string }> | undefined {
  const direct = lookupTagsByKey(tagDetailsByListId, leaf.leafId);
  if (direct?.length) return direct;
  const lead = findLeadsListForLeaf(leaf, leadsLists);
  return lead ? lookupTagsByKey(tagDetailsByListId, lead.voluumId) : undefined;
}

/** Resolves performance tags for a list row. Uses tagsByListId first, then tagsByWiserId via voluumId match. */
export function resolveTagsForListRow(
  row: { listId?: string; listName?: string },
  tagsByListId: Record<string, string[]>,
  tagsByWiserId: Record<number, string[]>,
  leadsLists: Array<{ id: number; name?: string; voluumId?: string | null }>,
): string[] {
  const listId = row.listId ? String(row.listId).trim() : '';
  if (!listId) return [];

  let tags =
    tagsByListId[listId] ??
    Object.entries(tagsByListId).find(
      ([k]) => k.toLowerCase() === listId.toLowerCase(),
    )?.[1] ??
    [];

  if (tags.length === 0) {
    const lead = leadsLists.find(
      (l) =>
        l.voluumId?.trim() &&
        l.voluumId.trim().toLowerCase() === listId.toLowerCase(),
    );
    if (lead) {
      const byWiserId = tagsByWiserId[lead.id];
      if (byWiserId?.length) tags = byWiserId;
    }
  }

  return tags;
}
